import { Form, Formik, FormikHelpers } from "formik";
import { useMemo } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { UserSelector } from "@/core/components/user-selector";
import type {
  IActivityFeedPricebookItemDetails,
  IEnrichedActivityFeedItem,
} from "@/activity-feed/activity-feed.d";
import { useBusiness } from "@/core/business";
import { formatMoneyCents } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import {
  SPIFF_RULE_EVENTS,
  UNDERLYING_RECORD_TYPES,
} from "@/spiff-rules/constants";
import {
  useGetActiveSpiffRules,
  useGetLatestSpiffRules,
} from "@/spiff-rules/query";
import {
  SPIFF_EVENT_BADGE_VARIANTS,
  SPIFF_EVENT_BORDER_COLORS,
} from "@/spiffs/constants";
import { useCreateSpiff } from "@/spiffs/query";

interface TGLAttributionContentProps {
  onClose: () => void;
  activity: IEnrichedActivityFeedItem;
}

interface TGLAttributionFormValues {
  userId: string;
}

const INITIAL_VALUES: TGLAttributionFormValues = {
  userId: "",
};

const validationSchema = Yup.object().shape({
  userId: Yup.string().required("Please select a user"),
});

export const TGLAttributionContent = ({
  onClose,
  activity,
}: TGLAttributionContentProps) => {
  const tglId = activity.underlying_record_id;
  const detailsJson =
    activity.details_json as IActivityFeedPricebookItemDetails;
  const baseAmountCents = detailsJson.percentage_base_amount_cents ?? 0;
  const tglNotes = detailsJson.display_identification;

  const { businessId } = useBusiness();
  const { mutateAsync: createSpiff, isPending } = useCreateSpiff(businessId);
  const { data: activeSpiffRules = [] } = useGetActiveSpiffRules(businessId);
  const { data: spiffRules = [] } = useGetLatestSpiffRules(
    activity.business_id,
  );
  const tglRule = useMemo(
    () =>
      spiffRules.find(
        (rule) => rule.event === SPIFF_RULE_EVENTS.tech_generated_lead,
      ),
    [spiffRules],
  );

  const hasActiveTGLSpiffRule = useMemo(
    () =>
      activeSpiffRules.some(
        (rule) =>
          rule.event === SPIFF_RULE_EVENTS.tech_generated_lead && rule.active,
      ),
    [activeSpiffRules],
  );

  if (!tglRule) {
    return null;
  }
  const spiffAmountcents = tglRule.payment_percent
    ? Math.min(
        (baseAmountCents * tglRule.payment_percent) / 100,
        tglRule.payment_percent_cap_cents ?? Infinity,
      )
    : (tglRule.payment_amount_cents ?? 0);

  const spiffAmount = formatMoneyCents(spiffAmountcents);

  const validEvent = tglRule.event as keyof typeof SPIFF_EVENT_BADGE_VARIANTS;

  const borderColorClass = SPIFF_EVENT_BORDER_COLORS[validEvent];
  const badgeVariant = validEvent
    ? SPIFF_EVENT_BADGE_VARIANTS[validEvent]
    : "gray";

  const handleSubmit = async (
    values: TGLAttributionFormValues,
    { setSubmitting }: FormikHelpers<TGLAttributionFormValues>,
  ) => {
    try {
      if (!values.userId) {
        toast.error("Please select a user");
        setSubmitting(false);
        return;
      }

      if (!hasActiveTGLSpiffRule) {
        toast.error("No active TGL spiff rule found");
        setSubmitting(false);
        return;
      }

      await createSpiff({
        userId: values.userId,
        triggerId: tglId,
        triggerType: UNDERLYING_RECORD_TYPES.SPIFF_TRIGGER_PLACEHOLDER,
        event: SPIFF_RULE_EVENTS.tech_generated_lead,
        percentageBaseAmountCents: baseAmountCents,
      });

      toast.success("Successfully created spiff");
      onClose();
    } catch {
      toast.error("Failed to create spiff");
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add user to TGL</DialogTitle>
      </DialogHeader>
      <Stack className="px-6">
        <Stack
          direction="row"
          align="center"
          className={cn("border-l-4 pl-4", borderColorClass)}
          fullWidth
        >
          <Stack gap="sm" className="flex-1">
            <Typography variant="subtitleW3">
              Tech Generated Lead Spiff
            </Typography>

            <Typography variant="captionW1" className="text-tertiary-text">
              {tglNotes} for {formatMoneyCents(baseAmountCents)}
            </Typography>
          </Stack>
          <Stack gap="xs" align="end">
            <Badge variant={badgeVariant} size="lg" className="py-2">
              {spiffAmount}
            </Badge>
          </Stack>
        </Stack>
      </Stack>

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Stack gap="lg" className="p-6 pt-0" fullWidth>
              <UserSelector
                businessId={businessId}
                value={values.userId}
                onChange={(value) => setFieldValue("userId", value)}
                placeholder="Select a user"
                fullWidth
              />
            </Stack>

            <DialogFooter>
              <Button type="button" variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={isPending}>
                Create Spiff
              </Button>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </>
  );
};
